import { useTranslation } from 'react-i18next';
import { ConciergeBell } from 'lucide-react';

/**
 * The one thing a guest needs to know while their order waits on the floor:
 * a person is walking over to read it back, and the kitchen has not started.
 *
 * Deliberately not a step on the journey list. The journey is the kitchen's
 * progress and every entry on it is a thing a cook does, so an extra tick there
 * would read as work already happening; this is a pause *before* that list, and
 * it replaces the list rather than joining it.
 *
 * Deliberately no time on it either. The venue's confirmation SLA arrives on
 * the session and stays there: a countdown at a table is a promise, and a
 * promise that runs out while nobody has come is worse than saying nothing.
 *
 * Amber-on-soft rather than the notice palette, which this app reserves for
 * things that have gone wrong — a stale menu, a sold-out line, a late order.
 * Nothing has gone wrong here; this is the venue working as designed.
 */
export function WaiterOnTheWay() {
  const { t } = useTranslation();

  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-line bg-accent-soft p-3">
      <ConciergeBell className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium text-accent">{t('guest.track.awaitingWaiter.title')}</p>
        <p className="text-xs text-muted-foreground">{t('guest.track.awaitingWaiter.body')}</p>
      </div>
    </div>
  );
}

/**
 * The same fact at pill size, for the row where the order-status pill would
 * otherwise sit. It stands in for that pill rather than joining it: two badges
 * on one row, one naming a kitchen state and one naming a waiter, is the exact
 * ambiguity this screen is meant to remove.
 */
export function WaiterOnTheWayBadge() {
  const { t } = useTranslation();

  return (
    <span className="rounded-full border border-line bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent">
      {t('guest.track.awaitingWaiter.badge')}
    </span>
  );
}
